import { Tag, Input, Typography, Skeleton, Card } from "antd";
import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SearchOutlined } from '@ant-design/icons';
import SpaceNewCard from "./components/SpaceNewCard";

import "../../style/space.css"
import BreadCrumb from "../BreadCrumb";
import { getSingleMasterData } from "../../services/adminServices";
import { getUserSpaces } from "../../services/SpaceServices";
import { setUserSpaces } from "../../redux/slices/userSlice";
import { useTranslation } from "react-i18next";

const { Title } = Typography;




const AllSpacesPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { userInfo, userGroupSpaces = [], userSpaces = [] } = useSelector(state => state.user);
    const [spaces, setSpaces] = useState(userSpaces);
    const [favouriteSpaces, setFavouriteSpaces] = useState([]);
    const [filteredSpaces, setFilteredSpaces] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loadingSpaces, setLoadingSpaces] = useState(userSpaces.length === 0);
    const favoriteSpaces = userInfo?.favoriteSpaces || [];
    const [defaultSpaces, setDefaultSpaces] = useState(0);

    useEffect(() => {
        if (userSpaces.length > 0) {
            setSpaces(userSpaces);
        }
    }, [userSpaces]);

    useEffect(() => {
        if (userSpaces.length > 0) {
            setLoadingSpaces(false);
            return;
        }
        const fetchUserSpaces = async () => {
            if (!userInfo?.email) return;
            setLoadingSpaces(true);
            try {
                const response = await getUserSpaces(userInfo.email);
                if (response.status === 200 && Array.isArray(response.data)) {
                    setSpaces(response.data);
                    dispatch(setUserSpaces(response.data));
                }
            } finally {
                setLoadingSpaces(false);
            }
        };
        fetchUserSpaces();
    }, [userInfo?.email]);

    const spaceBreadcrumb = [
        { title: t('Dashboard'), path: userInfo.isAdmin ? "/app/dashboard" : "/app/dashboard" },
        { title: t('All Spaces') }
    ];

    useEffect(() => {
        let favSpaces = spaces.filter(space => favoriteSpaces && favoriteSpaces.includes(space.spaceId));
        setFavouriteSpaces(favSpaces)
        let nonFavSpaces = spaces.filter(space => !favoriteSpaces || !favoriteSpaces.includes(space.spaceId))
        setFilteredSpaces(nonFavSpaces)
    }, [spaces, defaultSpaces])

    const fetchCategory = useCallback(async () => {
        let categoryResponse = await getSingleMasterData("", 'Category');
        if (categoryResponse?.data?.data?.length) {
            let categoryData = categoryResponse.data.data;
            categoryData = categoryData.map(data => {
                return { label: data["Name"], value: data["Name"] }
            })
            setCategories(categoryData)
        }
    }, [])


    useEffect(() => {
        fetchCategory();
    }, [fetchCategory])

   useEffect(() => {
        let reqFavSpaces = spaces.filter(space => favoriteSpaces && favoriteSpaces.includes(space.spaceId))
        let reqFilSpaces = spaces.filter(space => !favoriteSpaces || !favoriteSpaces.includes(space.spaceId))
        if (searchText) {
            reqFavSpaces = favouriteSpaces.filter((s) => {
                let spaceName = s.name?.toLowerCase() || "";
                let spaceDesc = s.description?.toLowerCase() || "";
                let enteredValue = searchText.toLowerCase();
                return spaceName.includes(enteredValue) || spaceDesc.includes(enteredValue)
            })
            reqFilSpaces = filteredSpaces.filter((s) => {
                let spaceName = s.name?.toLowerCase();
                let spaceDesc = s.description?.toLowerCase() || "";
                let enteredValue = searchText.toLowerCase();
                return spaceName.includes(enteredValue) || spaceDesc.includes(enteredValue)

            })
        } 
        if(selectedCategories.length > 0) {
            reqFilSpaces = reqFilSpaces.filter(space =>
                space.category && selectedCategories.some(category => space.category.includes(category))
            );
            reqFavSpaces = reqFavSpaces.filter(space =>
                space.category && selectedCategories.some(category => space.category.includes(category))
            );
        }
        setFavouriteSpaces(reqFavSpaces);
        setFilteredSpaces(reqFilSpaces)
        
    }, [searchText, selectedCategories])

    const searchHandler = (e) => {
        setSearchText(e.target.value)
    }

    const handleCategoryFilter = (category) => {
        let updatedSelectedCategories = [...selectedCategories]
        if (selectedCategories.includes(category)) {
            let catIndex = selectedCategories.indexOf(category);
            updatedSelectedCategories.splice(catIndex, 1);
        } else {
            updatedSelectedCategories.push(category)
        }
        setSelectedCategories(updatedSelectedCategories);
    };

    return (
        <div className="max-w-full p-3 md:p-4 lg:p-6">
            <div className="app-breadcrumb mb-4">
                <BreadCrumb items={spaceBreadcrumb} />
            </div>

            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-5 gap-3">
                <div>
                    <Title level={3} className="!m-0 !text-lg md:!text-xl !font-semibold text-gray-900">
                        {t("Your Spaces")}
                    </Title>
                </div>
                <div className="flex justify-end">
                    <Input
                        placeholder={t("Search spaces")}
                        allowClear
                        onChange={searchHandler}
                        suffix={<SearchOutlined className="mr-2 text-gray-400" />}
                        className="w-full md:w-56"
                        size="middle"
                    />
                </div>
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-2 mb-6">
                {loadingSpaces ? (
                    [1, 2, 3, 4].map((item) => (
                        <Skeleton.Button key={item} active size="small" style={{ width: 90, borderRadius: 8 }} />
                    ))
                ) : spaces.length > 0 ? (
                    categories.map(category => (
                        <button
                            key={category.value}
                            onClick={() => handleCategoryFilter(category.value)}
                            className={`px-3 py-1.5 text-sm rounded-lg border transition-all duration-200 ${selectedCategories.includes(category.value)
                                    ? 'bg-blue-500 text-white border-blue-500 shadow-sm'
                                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500 hover:text-blue-500'
                                }`}
                        >
                            {t(category.label)}
                        </button>
                    ))
                ) : (
                    <Title level={5} className="!m-0 !text-sm !font-normal text-gray-500">
                        {t("No spaces available.")}
                    </Title>
                )}
            </div>

            {loadingSpaces ? (
                <div>
                    <div className="flex items-center gap-2 mb-4">
                        <Title level={4} className="!m-0 !text-base !font-semibold text-gray-900">
                            {t("All Spaces")}
                        </Title>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
                        {[1, 2, 3, 4, 5, 6].map((item) => (
                            <Card key={item} className="w-full" style={{ minHeight: 180 }}>
                                <Skeleton avatar={{ shape: 'square', size: 48 }} active paragraph={{ rows: 2 }} />
                            </Card>
                        ))}
                    </div>
                </div>
            ) : (
                <>
                    {/* Favourite Spaces Section */}
                    {favouriteSpaces.length > 0 && (
                        <div className="mb-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Title level={4} className="!m-0 !text-base !font-semibold text-gray-900">
                                    {t("Favourite Spaces")}
                                </Title>
                                <Tag color="gold" className="text-xs">
                                    {favouriteSpaces.length}
                                </Tag>
                            </div>
                            <MySpaceList spaces={favouriteSpaces} userInfo={userInfo} userGroupSpaces={userGroupSpaces} />
                        </div>
                    )}

                    {/* All Spaces Section */}
                    <div>
                        <div className="flex items-center gap-2 mb-4">
                            <Title level={4} className="!m-0 !text-base !font-semibold text-gray-900">
                                {t("All Spaces")}
                            </Title>
                            <Tag color="blue" className="text-xs">
                                {filteredSpaces.length}
                            </Tag>
                        </div>
                        <MySpaceList spaces={filteredSpaces} userInfo={userInfo} userGroupSpaces={userGroupSpaces} />
                    </div>
                </>
            )}
        </div>
    );
};

export default AllSpacesPage;

const MySpaceList = ({ spaces, userInfo, userGroupSpaces }) => {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
            {spaces.map((space) => {
                return (
                    <div key={space.spaceId} className="w-full">
                        <SpaceNewCard {...space} amISpaceManager={true} userInfo={userInfo} userGroupSpaces={userGroupSpaces} />
                    </div>
                );
            })}
        </div>
    );
};